/**
 * CDM Correlation Rules — admin surface for the cross-domain engine.
 *
 * Lists every correlation rule (global defaults + org overrides) with a
 * per-row enable/disable toggle and a "run now" button. Six day-one
 * rules ship pre-seeded; all are global (org_id IS NULL) until an admin
 * makes the first change, at which point the row is cloned-on-write
 * into an org-specific override.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, RefreshCw, ChevronLeft, Play, ToggleLeft, ToggleRight, Globe,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface RuleRow {
  id: string
  org_id: string | null
  name: string
  display_name: string
  dsl: string
  rule_kind: string
  config: Record<string, unknown>
  window_seconds: number
  min_co_occurrence: number
  min_severity: string | null
  action: string
  enabled: boolean
  version: number
  last_evaluated_at: string | null
  last_match_count: number
}

const ACTION_TONE: Record<string, string> = {
  alert:           'bg-cyan/10 text-cyan border-cyan/30',
  open_incident:   'bg-rose/10 text-rose-light border-rose/30',
  raise_conflict:  'bg-amber/10 text-amber-light border-amber/30',
  agentic_triage:  'bg-purple/10 text-purple-light border-purple/30',
}

function fmtWindow(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}

export default function CdmCorrelationRulesPage() {
  const [rules, setRules] = useState<RuleRow[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cdm/correlation-rules')
      setRules((r.data?.rules || []) as RuleRow[])
    } catch {
      setRules([])
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { void load() }, [])

  const runOne = async (id: string) => {
    setBusy(id)
    setMessage('Evaluating rule…')
    try {
      const r = await api.post(`/api/cdm/correlation-rules/${id}/run`)
      const s = r.data?.summary as { inserted: number; deduped: number } | undefined
      setMessage(`Done. inserted=${s?.inserted ?? 0} deduped=${s?.deduped ?? 0}`)
      await load()
    } catch {
      setMessage('Run failed — admin role required')
    } finally {
      setBusy(null)
    }
  }

  const toggle = async (id: string, enabled: boolean) => {
    setBusy(id)
    try {
      await api.post(`/api/cdm/correlation-rules/${id}/toggle`, { enabled })
      await load()
    } catch {
      setMessage('Toggle failed — admin role required')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm/correlations" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Correlations
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Activity size={18} className="text-cyan" /> CORRELATION RULES
        </h1>
        <span className="text-[10px] text-text-muted">
          {rules.length} total · {rules.filter(r => r.enabled).length} enabled
        </span>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {message && (
        <div className="card p-2 text-[11px] text-text-secondary">{message}</div>
      )}

      <div className="card p-3">
        {loading && <div className="text-text-muted text-xs">Loading…</div>}
        {!loading && rules.length === 0 && (
          <div className="text-text-muted text-xs">No correlation rules — bootstrap may not have run yet.</div>
        )}
        <div className="space-y-2">
          {rules.map(r => (
            <div key={r.id} className="p-2 rounded border border-border bg-surface-card space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => void toggle(r.id, !r.enabled)}
                  disabled={busy === r.id}
                  className="text-text-muted hover:text-text-primary"
                  aria-label={r.enabled ? 'Disable rule' : 'Enable rule'}
                >
                  {r.enabled
                    ? <ToggleRight size={18} className="text-emerald" />
                    : <ToggleLeft size={18} className="text-text-muted" />}
                </button>
                <code className="text-xs font-mono font-bold text-text-primary">{r.name}</code>
                {r.org_id === null && (
                  <span className="text-[9px] text-text-muted inline-flex items-center gap-1">
                    <Globe size={9} /> global
                  </span>
                )}
                <span className={clsx('text-[10px] px-2 py-0.5 rounded border',
                                      ACTION_TONE[r.action] || ACTION_TONE.alert)}>
                  {r.action}
                </span>
                <span className="text-[10px] text-text-muted">window {fmtWindow(r.window_seconds)}</span>
                <span className="text-[10px] text-text-muted">min co-occur {r.min_co_occurrence}</span>
                {r.min_severity && (
                  <span className="text-[10px] text-text-muted">≥ {r.min_severity}</span>
                )}
                <button
                  type="button"
                  onClick={() => void runOne(r.id)}
                  disabled={busy === r.id || !r.enabled}
                  className="btn-ghost ml-auto"
                >
                  <Play size={11} className={busy === r.id ? 'animate-spin' : ''} /> Run now
                </button>
              </div>
              <div className="text-[11px] text-text-secondary">{r.display_name}</div>
              <div className="text-[10px] text-text-muted font-mono whitespace-pre-wrap break-all">{r.dsl}</div>
              <div className="text-[10px] text-text-muted">
                last_evaluated_at {r.last_evaluated_at
                  ? new Date(r.last_evaluated_at).toLocaleString()
                  : 'never'} · last match count {r.last_match_count}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
